import ContactForm from "../components/ContactForm";
import SiteHeader from "../components/SiteHeader";
import TrackedLink from "../components/TrackedLink";
import { customerConnectivity } from "../content/clientResults";

const profilePhoto = "/ebrahim-ahmadivarzaneh-portrait.jpg";

type ServiceMarkType = "build" | "improve" | "migration" | "connectivity" | "troubleshooting";

function ServiceMark({ type }: { type: ServiceMarkType }) {
  return (
    <span className="service-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48">
        {type === "build" && (
          <path d="m8 17 16-9 16 9-16 9-16-9Zm0 8 16 9 16-9M8 33l16 9 16-9" />
        )}
        {type === "improve" && (
          <>
            <path d="M12 8v12m0 8v12M24 8v4m0 8v20M36 8v20m0 8v4" />
            <circle cx="12" cy="24" r="4" />
            <circle cx="24" cy="16" r="4" />
            <circle cx="36" cy="32" r="4" />
          </>
        )}
        {type === "migration" && (
          <>
            <path d="M15 36h21a8 8 0 0 0 1-16 13 13 0 0 0-25-1 9 9 0 0 0 3 17Z" />
            <path d="M24 31V18m-5 5 5-5 5 5" />
          </>
        )}
        {type === "connectivity" && (
          <>
            <path d="m20 16 5-5a8.5 8.5 0 0 1 12 12l-5 5M28 32l-5 5a8.5 8.5 0 0 1-12-12l5-5" />
            <path d="m17 31 14-14" />
          </>
        )}
        {type === "troubleshooting" && (
          <>
            <circle cx="21" cy="21" r="14" />
            <path d="m31 31 10 10M11 22h5l4-7 4 12 3-5h4" />
          </>
        )}
      </svg>
    </span>
  );
}

export default function Home() {
  return (
    <main>
      <SiteHeader />

      <section className="hero" id="top">
        <div className="hero-copy">
          <h1>
            I build on AWS.{" "}
            <span>And fix production problems.</span>
          </h1>
          <p className="hero-intro">
            I work directly with founders and CTOs on AWS migrations, cloud
            networking, and troubleshooting across infrastructure, databases
            and applications. I stay hands-on from architecture and investigation
            through implementation.
          </p>
          <div className="hero-actions">
            <TrackedLink
              className="button button-primary"
              href="#contact"
              eventName="Hero Contact Click"
            >
              Discuss your AWS project <span aria-hidden="true">↗</span>
            </TrackedLink>
            <TrackedLink
              className="button button-secondary"
              href="#work"
              eventName="Hero Work Click"
            >
              See my work
            </TrackedLink>
          </div>
        </div>

        <aside className="portrait-card" aria-label="About Ebrahim Ahmadivarzaneh">
          <div className="portrait-frame">
            <img src={profilePhoto} alt="Ebrahim Ahmadivarzaneh" width="1200" height="1800" />
          </div>
          <div className="portrait-context">
            <strong>Finland · Remote</strong>
          </div>
          <div className="portrait-caption">
            <div>
              <strong>Ebrahim Ahmadivarzaneh</strong>
              <span>Independent AWS consultant</span>
            </div>
          </div>
        </aside>
      </section>

      <section className="homepage-outcomes" id="proof" aria-labelledby="homepage-outcomes-label">
        <p id="homepage-outcomes-label">Results delivered in client environments</p>
        <div className="proof-strip">
          <article><strong>~$50k / year</strong><span>annualised RDS cost savings</span></article>
          <article><strong>90 → 20 min</strong><span>main production deployment</span></article>
          <article><strong>35–40%</strong><span>lower database load</span></article>
        </div>
      </section>

      <section className="services-section" id="services">
        <div className="section services-inner">
          <div className="section-heading services-heading">
            <p className="section-kicker">01 / Services</p>
            <div className="services-heading-copy">
              <h2>I usually join at one of two points.</h2>
            </div>
          </div>

          <div className="service-entry-grid">
            <article className="service-entry-card">
              <ServiceMark type="build" />
              <div className="service-entry-copy">
                <p className="service-entry-label">BUILD</p>
                <h3>Starting on AWS</h3>
                <p>A production environment your team can deploy to and operate.</p>
              </div>
            </article>

            <article className="service-entry-card">
              <ServiceMark type="improve" />
              <div className="service-entry-copy">
                <p className="service-entry-label">IMPROVE</p>
                <h3>Improving what’s live</h3>
                <p>Changes and fixes for a platform your customers already depend on.</p>
              </div>
            </article>
          </div>

          <nav
            className="service-offerings"
            id="common-improvement-areas"
            aria-label="Consulting services"
          >
            <p className="service-offerings-heading">Where I can help</p>
            <div className="service-offerings-grid">
              <TrackedLink
                className="service-offering"
                href="/aws-platform-consulting"
                eventName="Service Page Click"
                eventProps={{ service: "platform-modernization" }}
              >
                <ServiceMark type="migration" />
                <h3>AWS architecture &amp; engineering</h3>
                <p>Design new systems or improve your applications, cloud networking and delivery.</p>
                <span className="service-offering-link">
                  Explore service <span aria-hidden="true">↗</span>
                </span>
              </TrackedLink>

              <TrackedLink
                className="service-offering"
                href="/aws-observability-consulting"
                eventName="Service Page Click"
                eventProps={{ service: "reliability-performance-cost" }}
              >
                <ServiceMark type="troubleshooting" />
                <h3>Production troubleshooting</h3>
                <p>Understand what is happening across your system and resolve the cause of outages or slow performance.</p>
                <span className="service-offering-link">
                  Explore service <span aria-hidden="true">↗</span>
                </span>
              </TrackedLink>
            </div>
          </nav>
        </div>
      </section>

      <section className="work-section" id="work">
        <div className="section work-inner">
          <div className="section-heading section-heading-light">
            <p className="section-kicker">02 / Selected Work</p>
            <div className="work-heading-copy">
              <h2>Problems I’ve solved.</h2>
            </div>
          </div>

          <div className="work-projects">
            <div className="work-column-headings" aria-hidden="true">
              <span>The problem</span>
              <span>What I changed</span>
              <span>The result</span>
            </div>

            <article className="work-project">
              <div className="work-project-problem">
                <ServiceMark type="migration" />
                <h3>Slow production deployments</h3>
              </div>
              <p className="work-project-action">
                I rebuilt containers and deployment workflows while moving live
                workloads to ECS.
              </p>
              <div className="work-project-result">
                <strong>90 → 20 min</strong>
                <span>main production deployment</span>
              </div>
            </article>

            <article className="work-project">
              <div className="work-project-problem">
                <ServiceMark type="connectivity" />
                <h3>Customer sites were hard to connect</h3>
              </div>
              <p className="work-project-action">
                {customerConnectivity.change}
              </p>
              <div className="work-project-result work-project-result-text">
                <strong>{customerConnectivity.setupTime.before} → {customerConnectivity.setupTime.after}</strong>
                <span>{customerConnectivity.setupTime.label}</span>
              </div>
            </article>

            <article className="work-project">
              <div className="work-project-problem">
                <ServiceMark type="troubleshooting" />
                <h3>High database costs and timeouts</h3>
              </div>
              <p className="work-project-action">
                I tuned storage and retention, and worked with developers to
                fix inefficient queries.
              </p>
              <div className="work-project-result">
                <strong>$7k → $2.7k</strong>
                <span>monthly RDS running costs</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="about-section" id="about">
        <div className="section about-inner about-inner-split">
          <div className="about-copy">
            <p className="section-kicker">03 / About Ebrahim Ahmadivarzaneh</p>
            <h2>From first platform to complex production.</h2>
            <p>
              With 6+ years across infrastructure, networking and software
              delivery, I help solo founders launch their first platform and
              larger engineering organizations resolve reliability, performance
              and deployment problems.
            </p>
            <p>
              I handle the investigation and implementation myself.{' '}
              <strong>Your work isn’t passed down to another subcontractor.</strong>
            </p>
            <p>
              <strong>I’m available for short projects, fixed-scope work and longer-term engagements.</strong>
            </p>
          </div>

          <aside className="about-details" aria-label="Engagements and credentials">
            <div className="credibility-item">
              <strong>B2B engagements</strong>
              <span>Europe &amp; US time zones</span>
            </div>

            <div className="credentials-block" aria-label="Certifications">
              <p className="about-detail-label">Certifications</p>
              <div className="credential-list">
                <div className="credential-item">
                  <img
                    className="credential-badge"
                    src="/aws-certified-advanced-networking-specialty.png"
                    alt=""
                    width="38"
                    height="38"
                    loading="lazy"
                    decoding="async"
                  />
                  <strong>AWS Certified Advanced Networking – Specialty</strong>
                </div>
                <div className="credential-item">
                  <img
                    className="credential-badge"
                    src="/aws-certified-devops-engineer-professional.png"
                    alt=""
                    width="38"
                    height="38"
                    loading="lazy"
                    decoding="async"
                  />
                  <strong>AWS Certified DevOps Engineer – Professional</strong>
                </div>
                <div className="credential-item">
                  <img
                    className="credential-badge"
                    src="/aws-certified-sysops-administrator-associate.png"
                    alt=""
                    width="38"
                    height="38"
                    loading="lazy"
                    decoding="async"
                  />
                  <strong>AWS Certified SysOps Administrator – Associate</strong>
                </div>
              </div>
            </div>

          </aside>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div className="contact-copy">
          <div className="contact-orbit" aria-hidden="true"><span /><span /><span /></div>
          <p className="section-kicker">04 / Start here</p>
          <h2>Have a platform problem that needs an owner?</h2>
          <p>Tell me what you’re working on and where you could use some help. A few sentences are enough.</p>
          <div className="direct-contact">
            <span>What happens next</span>
            <p>I’ll reply personally so we can discuss whether I can help and what a useful next step might be.</p>
          </div>
        </div>
        <ContactForm
          contextLabel="Your project"
          contextPrompt="A few details to see if I can help."
          messagePlaceholder="What are you working on, and where could you use some help? Include any timing you have in mind."
        />
      </section>

      <footer className="minimal-footer">
        <span>© 2026 Ebi Consulting Oy · Y-tunnus 3597750-8</span>
        <nav aria-label="Footer navigation"><a href="#services">Services</a><a href="#work">Work</a><a href="#about">About</a><a href="#top">Top</a><a href="https://www.linkedin.com/in/ebihimself/">LinkedIn</a></nav>
      </footer>
    </main>
  );
}
